<template>
  <div>
    <only-site-card :com_data="com_data">
      <template v-slot:item_edit>
        <ValidationObserver ref="observer">
          <v-container fluid>
            <!-- マスタ読込中 -->
            <div v-if="masterLoadingFlg" class="mb-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                width="2"
              ></v-progress-circular>
              <span class="ml-2"
                >コース・オプション・ラベルを取得しています…</span
              >
            </div>
            <!-- 取得失敗 -->
            <div v-else-if="masterError" class="red--text mb-8">
              コース・オプション・ラベルの取得に失敗しました。時間をおいて再度お試しください。
            </div>

            <!-- ラベル（媒体の店舗設定「スタッフラベル」で追加・編集できる可変項目） -->
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">ラベル</span>
                <p class="mini">
                  ラベルの追加・編集は媒体の店舗設定から行います。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row class="mt-2 mb-2">
                  <v-col
                    v-for="label in labelList"
                    :key="'label_' + label.id"
                    cols="6"
                    sm="4"
                    md="3"
                  >
                    <v-checkbox
                      class="mt-1"
                      v-model="selectedLabels"
                      name="labelIds"
                      :value="label.id"
                      :label="label.name"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col v-if="labelList.length === 0" cols="12">
                    <span class="mini">登録されているラベルがありません。</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>

            <!-- 単一入力項目（役職・SNS URL・メールアドレス） -->
            <div v-for="key in textItemKeys" :key="key">
              <v-row>
                <v-col cols="12" sm="2">
                  <span class="font-weight-bold">{{ items[key].text }}</span>
                  <p v-if="items[key].hint" class="mini">
                    {{ items[key].hint }}
                  </p>
                </v-col>
                <v-col cols="12" sm="10">
                  <v-row>
                    <v-col cols="12" sm="6">
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        :name="items[key].text"
                        :rules="items[key].rules"
                      >
                        <v-text-field
                          :ref="key"
                          v-model="cast_prop[key]"
                          :name="key"
                          :error-messages="errors"
                          :success="valid"
                          :label="items[key].text"
                          :placeholder="items[key].placeholder"
                          outlined
                          background-color="white"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-divider class="mt-4 mb-8"></v-divider>
            </div>

            <!-- インターバル上書き（媒体と同じくセレクトボックスで選ぶ） -->
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">{{
                  items.intervalOverride.text
                }}</span>
                <p class="mini">{{ items.intervalOverride.hint }}</p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="items.intervalOverride.text"
                      :rules="items.intervalOverride.rules"
                    >
                      <v-select
                        ref="intervalOverride"
                        v-model="cast_prop.intervalOverride"
                        name="intervalOverride"
                        :items="intervalItem"
                        :error-messages="errors"
                        :success="valid"
                        :label="items.intervalOverride.text"
                        outlined
                        background-color="white"
                      ></v-select>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>

            <!-- 料金・報酬額（媒体と同じく種別ごとにまとめて表示する） -->
            <div v-for="group in feeGroups" :key="group.key">
              <v-row>
                <v-col cols="12" sm="2">
                  <span class="font-weight-bold">{{ group.title }}</span>
                  <p class="mini">{{ group.hint }}</p>
                </v-col>
                <v-col cols="12" sm="10">
                  <v-row v-for="key in group.keys" :key="key">
                    <v-col cols="12" sm="6" md="3">
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        :name="items[key].text"
                        :rules="items[key].rules"
                      >
                        <v-text-field
                          :ref="key"
                          v-model="cast_prop[key]"
                          :name="key"
                          :error-messages="errors"
                          :success="valid"
                          :label="items[key].text"
                          suffix="円"
                          outlined
                          background-color="white"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-divider class="mt-4 mb-8"></v-divider>
            </div>

            <!-- 対応コース（媒体のコースマスタから動的に組み立てる可変項目） -->
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">対応コース</span>
                <p class="mini">
                  未選択のまま新規登録した場合、全コースを選択して登録します。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row class="mt-2 mb-2">
                  <v-col
                    v-for="course in courseList"
                    :key="'course_' + course.id"
                    cols="12"
                  >
                    <v-checkbox
                      class="mt-1"
                      v-model="selectedCourses"
                      name="supportedCourses"
                      :value="course.id"
                      :label="courseLabel(course)"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col v-if="courseList.length === 0" cols="12">
                    <span class="mini">登録されているコースがありません。</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>

            <!-- コース別報酬額（媒体のコースマスタから動的に組み立てる可変項目） -->
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">コース別報酬額</span>
                <p class="mini">
                  未入力のコースは媒体のメニュー管理の報酬額が使われます。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row
                  v-for="course in courseList"
                  :key="'course_reward_' + course.id"
                >
                  <v-col cols="12" sm="6" md="3">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="course.name"
                      rules="numeric|max:9"
                    >
                      <v-text-field
                        v-model="cast_prop['courseReward_' + course.id]"
                        :name="'courseReward_' + course.id"
                        :error-messages="errors"
                        :success="valid"
                        :label="course.name"
                        :placeholder="course.reward_amount"
                        suffix="円"
                        outlined
                        background-color="white"
                      ></v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>

            <!-- オプション別報酬額（媒体のオプションマスタから動的に組み立てる可変項目） -->
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">オプション別報酬額</span>
                <p class="mini">
                  未入力のオプションは媒体のオプション管理の報酬額が使われます。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row
                  v-for="option in optionList"
                  :key="'option_reward_' + option.id"
                >
                  <v-col cols="12" sm="6" md="3">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="option.name"
                      rules="numeric|max:9"
                    >
                      <v-text-field
                        v-model="cast_prop['optionReward_' + option.id]"
                        :name="'optionReward_' + option.id"
                        :error-messages="errors"
                        :success="valid"
                        :label="option.name"
                        :placeholder="option.reward_amount"
                        suffix="円"
                        outlined
                        background-color="white"
                      ></v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-8"></v-divider>
          </v-container>
        </ValidationObserver>
      </template>
    </only-site-card>
  </div>
</template>

<script>
import { set, reactive, toRefs, defineComponent } from "@vue/composition-api";
import { repositoryFactory } from "@/repository/repositoryFactory";
import store from "@/store/index.js";
import setting from "@/common/setting.js";
import utilFunc from "@/common/utilFunc.js";
import retryRequest from "@/common/retryRequest";

export default defineComponent({
  props: ["com_data"],
  setup(props, ctx) {
    const CastPropsRepository = repositoryFactory.get("castProps");
    const SynchroRepository = repositoryFactory.get("synchro");
    const state = reactive({
      errorElement: null,
      cast_prop: {},
      // チェックボックス表示用(配列)。cast_propのJSON配列文字列と相互変換する
      selectedLabels: [],
      selectedCourses: [],
      // 媒体のマスタ(店舗ごとに内容が変わる可変項目)
      courseList: [],
      optionList: [],
      labelList: [],
      masterLoadingFlg: false,
      masterError: false,
      // 読み込み時データ保持(保存前比較用)
      keepData: {
        cast_prop: {},
      },
    });

    // 媒体の編集画面と同じ並びで単一入力項目を表示する
    const textItemKeys = ["position", "snsUrl", "email"];

    // 料金・報酬額は媒体と同じく種別ごとにまとめて表示する
    const feeGroups = [
      {
        key: "fee",
        title: "指名料・本指名料・姫予約料",
        hint: "未入力の場合は媒体の店舗設定が使われます。",
        keys: ["nominationFee", "mainNominationFee", "himeFee"],
      },
      {
        key: "reward",
        title: "指名/本指名/姫予約の報酬額",
        hint: "未入力の場合は媒体の店舗設定が使われます。",
        keys: [
          "nominationRewardAmount",
          "mainNominationRewardAmount",
          "himeRewardAmount",
        ],
      },
    ];

    // インターバル上書きの選択肢(媒体のセレクトボックスと同じ値)
    const intervalItem = [
      { text: "未設定（メニュー設定を使用）", value: "" },
      { text: "5分", value: "5" },
      { text: "10分", value: "10" },
      { text: "15分", value: "15" },
      { text: "20分", value: "20" },
      { text: "25分", value: "25" },
      { text: "30分", value: "30" },
    ];

    // 文字数制限はサイト準拠で設けない(rules空)
    const items = {
      position: {
        text: "役職",
        rules: "",
        placeholder: "店長 / 主任セラピスト など",
        hint: "",
      },
      snsUrl: {
        text: "SNS URL",
        rules: "",
        placeholder: "https://twitter.com/...",
        hint: "",
      },
      email: {
        text: "メールアドレス",
        rules: "email",
        placeholder: "staff@example.com",
        hint: "媒体の予約通知・リマインドメールに使われます。",
      },
      intervalOverride: {
        text: "インターバル上書き",
        rules: "",
        placeholder: "",
        hint: "未設定の場合は媒体のメニュー設定が使われます。",
      },
      nominationFee: {
        text: "指名料",
        rules: "numeric|max:9",
      },
      mainNominationFee: {
        text: "本指名料",
        rules: "numeric|max:9",
      },
      himeFee: {
        text: "姫予約料",
        rules: "numeric|max:9",
      },
      nominationRewardAmount: {
        text: "指名報酬額",
        rules: "numeric|max:9",
      },
      mainNominationRewardAmount: {
        text: "本指名報酬額",
        rules: "numeric|max:9",
      },
      himeRewardAmount: {
        text: "姫予約報酬額",
        rules: "numeric|max:9",
      },
    };

    // 対応コースのラベル(媒体の表示に合わせてコース時間を添える)
    const courseLabel = (course) => {
      if (course.duration) {
        return course.name + "（" + course.duration + "分）";
      }
      return course.name;
    };

    // コース・オプション・ラベルのマスタをサイトから動的取得する
    // 報酬額のキーは courseReward_{コースID} / optionReward_{オプションID}
    // 媒体側でコースを並び替え・追加しても値が項目に正しく追従する
    const getMasterList = async () => {
      state.masterError = false;
      state.masterLoadingFlg = true;
      state.courseList.splice(0);
      state.optionList.splice(0);
      state.labelList.splice(0);
      try {
        const response = await retryRequest(
          SynchroRepository.sync_cast_only_forest_appt_master_list,
          {
            shop_id: store.getters["shops/currentShop"].id,
            shop_site_id: props.com_data.id,
          }
        );
        if (response.data) {
          const data = response.data.data;
          state.courseList.push(...data.course_list);
          state.optionList.push(...data.option_list);
          state.labelList.push(...data.label_list);
          // cast_propに未登録なら初期化（取込済みの値があればそのまま使う）
          const rewardKeys = [];
          data.course_list.forEach((course) => {
            rewardKeys.push("courseReward_" + course.id);
          });
          data.option_list.forEach((option) => {
            rewardKeys.push("optionReward_" + option.id);
          });
          rewardKeys.forEach((key) => {
            if (!(key in state.cast_prop)) {
              set(state.cast_prop, key, null);
              state.keepData.cast_prop[key] = null;
            }
          });
        }
      } catch (error) {
        state.masterError = true;
      } finally {
        state.masterLoadingFlg = false;
      }
    };

    // 画面表示用にstate.cast_propの値を整理(媒体コピー処理からreflectCastPropsで呼ばれる)
    const reflectCastProps = async () => {
      // ラベル・対応コースのcast_prop(JSON配列文字列)を表示用配列に変換
      [
        { key: "labelIds", target: "selectedLabels" },
        { key: "supportedCourses", target: "selectedCourses" },
      ].forEach((item) => {
        let arr = [];
        const value = state.cast_prop[item.key];
        if (value) {
          try {
            arr = JSON.parse(value);
          } catch (e) {
            arr = [];
          }
        }
        set(state, item.target, Array.isArray(arr) ? arr : []);
      });

      // インターバル上書きは未設定を空文字で扱う(セレクトの「未設定」に一致させる)
      set(
        state.cast_prop,
        "intervalOverride",
        state.cast_prop.intervalOverride ?? ""
      );
    };

    const init = async () => {
      // コース・オプション・ラベルのマスタを動的取得
      getMasterList();

      // cast_propにキーを設定
      for (let key in items) {
        set(state.cast_prop, key, null);
      }
      set(state.cast_prop, "labelIds", null);
      set(state.cast_prop, "supportedCourses", null);
      Object.keys(props.com_data.cast_prop).forEach(function (key) {
        set(state.cast_prop, key, props.com_data.cast_prop[key]);
      });
      // 読み込み時のデータを保持(保存前に比較するため)
      state.keepData.cast_prop = Object.assign({}, state.cast_prop);

      // 画面表示前の後処理
      await reflectCastProps();
    };

    init();

    // チェック処理
    const checkValid = async () => {
      state.errorElement = null;
      return await ctx.refs.observer.validate();
    };

    const setErrorFocus = async () => {
      if (!state.errorElement) return;
      setTimeout(() => {
        state.errorElement.scrollIntoView({
          block: "center",
        });
      }, 300);
    };

    // 保存前にcastPropsの値を整理する(媒体コピー処理からsetCastPropsで呼ばれる)
    const setCastProps = async () => {
      // 表示用配列をcast_prop(JSON配列文字列)に戻す
      [
        { key: "labelIds", target: "selectedLabels" },
        { key: "supportedCourses", target: "selectedCourses" },
      ].forEach((item) => {
        set(
          state.cast_prop,
          item.key,
          state[item.target].length === 0
            ? ""
            : JSON.stringify(state[item.target])
        );
      });
    };

    // 保存処理
    const save = async () => {
      // 保存前にcastPropsの値を整理する
      await setCastProps();

      // 更新前と比べて差分があれば更新処理
      if (!utilFunc.compareObject(state.keepData.cast_prop, state.cast_prop)) {
        const prop_param = [];
        for (let key in state.cast_prop) {
          prop_param.push({
            cast_id: props.com_data.cast_id,
            key: props.com_data.prefix + "__" + key,
            value: state.cast_prop[key] ?? "",
          });
        }
        await CastPropsRepository.save_all({ cast_prop: prop_param })
          .then((response) => {
            if (response.data) {
              // データ追加OK
            }
          })
          .catch((error) => {
            throw (
              "ERROR:forest_appt.vue/save CastPropsRepository.save_all (" +
              error +
              ")"
            );
          });
      }
      return true;
    };

    return {
      props,
      setting,
      items,
      textItemKeys,
      feeGroups,
      intervalItem,
      courseLabel,
      ...toRefs(state),
      checkValid,
      setErrorFocus,
      save,
      reflectCastProps,
      setCastProps,
    };
  },
});
</script>

<style scoped></style>
